import {I18n} from '@aws-amplify/core';
import {DataTableColumn} from 'mantine-datatable';
import {NavLink} from 'react-router-dom';

import {formatTimestamp} from '../../../utils/TableTimestamps';
import {
    Subscription,
    SubscriptionStatus,
} from '../types';
import {StatusBadge} from '../profile/StatusBadge';
import {CustomerLink} from '../links/CustomerLink';

// FNP-918: was `tables/columns.tsx` — a bare `getColumns()` array where EVERY
// column was `render`-only, with no `accessor` on any of them.
//
// That is the distinction that made this module unsortable. `render` controls
// how a cell LOOKS; `accessor` is what sorting sorts BY. A render-only config
// looks complete in review and makes sorting structurally impossible — you
// cannot add `sortable: true` to a column that has nothing to sort on. Moved
// into a hook to match the platform shape (checklist §3z).
//
// ⚠️ `created` and `startDate` on a Stripe subscription are UNIX EPOCHS IN
// SECONDS, not AWSDateTime strings. The old columns multiplied by 1000 inline
// for display; those conversions now live in `createdAtMillis` /
// `startDateMillis` on the record (see useSubscriptions.ts) so DISPLAY and SORT
// agree. Sorting the raw seconds would still order correctly, but the two would
// silently drift apart the moment either side changed.
//
// ⚠️ The old Subscription ID column was a `<span>` with an inline
// `cursor: pointer` that opened an edit drawer — a fake link, unreachable by
// keyboard and invisible to a screen reader (§13 accessibility). It is a real
// NavLink to the detail page now, matching every sibling module.
export const useSubscriptionsTableColumns = () => {

    const columns: DataTableColumn<Subscription>[] = [
        {
            accessor: 'id',
            sortable: true,
            title: I18n.get('Subscription ID'),
            width: '1fr',
            render: (item: Subscription) => (
                <NavLink to={`/subscriptions/${encodeURIComponent(item.id)}`}>
                    {item.id}
                </NavLink>
            ),
        },
        {
            accessor: 'customerId',
            sortable: true,
            title: I18n.get('Customer'),
            width: '1fr',
            // Nullable on the Stripe API. CustomerLink resolves the id to a
            // name; the em-dash placeholder covers the absent case (§13).
            render: (item: Subscription) => (item.customerId
                ? <CustomerLink customer={item.customerId}/>
                : '—'),
        },
        {
            // Sorts on the normalised millisecond value so the ordering matches
            // what the cell displays. `startDate` (seconds) is still the source.
            accessor: 'startDateMillis',
            sortable: true,
            title: I18n.get('Start Date'),
            width: '1fr',
            render: (item: Subscription) => formatTimestamp(item.startDateMillis),
        },
        {
            accessor: 'status',
            sortable: true,
            title: I18n.get('Status'),
            width: '1fr',
            // Enum state renders as a badge, not a raw string (§13).
            render: (item: Subscription) => (item.status
                ? <StatusBadge status={item.status as SubscriptionStatus}/>
                : '—'),
        },
        {
            accessor: 'createdAtMillis',
            sortable: true,
            title: I18n.get('Created'),
            width: '1fr',
            render: (item: Subscription) => formatTimestamp(item.createdAtMillis),
        },
    ];

    return {columns};
};
